function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function round(value, digits = 2) {
  const p = 10 ** digits;
  return Math.round((Number(value) || 0) * p) / p;
}

function pickMainBottleneck(scores) {
  const entries = Object.entries(scores || {});
  if (!entries.length) return "Sem gargalo";
  entries.sort((a, b) => b[1] - a[1]);
  const [name, score] = entries[0];
  if ((score || 0) <= 0) return "Sem gargalo";
  const labels = {
    operators: "Operadores",
    maintenance: "Manutenção",
    losses: "Perdas",
    risk: "Risco",
    planLoad: "Carga de plano",
  };
  return labels[name] || name;
}

export function calculateMachineSimulationRow({
  machine,
  period,
  planValue,
  realizedValue,
  parameters,
  operatorBaseline,
  scheduledMaintenanceHours = 0,
  effectiveContractHours,
  machineOperatorCalendarFactor = 1,
}) {
  const contractHours =
    Number.isFinite(Number(effectiveContractHours))
      ? Number(effectiveContractHours)
      : Number(parameters?.contractHours) || 0;
  const maintenanceHours = Number(parameters?.maintenanceHours) || 0;
  const lossesPercent = Number(parameters?.lossesPercent) || 0;
  const riskFactor = Number(parameters?.riskFactor) || 0;
  const productivityAvg = Number(parameters?.productivityAvg) || 0;
  const operatorCount = Number(parameters?.operatorCount) || 0;
  const tolerancePercent = Number(parameters?.tolerancePercent) || 0;

  const lossesHours = contractHours * (lossesPercent / 100);
  const totalMaintenanceHours =
    Math.max(0, maintenanceHours) + Math.max(0, Number(scheduledMaintenanceHours) || 0);
  const adjustedMaintenanceHours =
    totalMaintenanceHours * (machine.maintenanceSensitivity || 1);
  const availableHours = Math.max(
    0,
    contractHours - adjustedMaintenanceHours - lossesHours
  );

  const riskPenalty = clamp(
    (riskFactor / 100) * (machine.riskSensitivity || 0.2),
    0,
    0.35
  );
  const machineAvgPerHour =
    Number(machine?.avgPerHour ?? machine?.averagePerHour) || productivityAvg || 0;
  const productivityApplied =
    machineAvgPerHour * (machine.productivityFactor || 1) * (1 - riskPenalty);

  const recommendedOperators = Number(operatorBaseline?.recommendedOperators) || 1;
  const avgRequiredPerMachine = Number(operatorBaseline?.avgRequiredPerMachine) || 1;

  const globalOperatorFactor = clamp(operatorCount / recommendedOperators, 0.55, 1.15);
  const machineOperatorWeight = clamp(
    avgRequiredPerMachine / Math.max(1, Number(machine.requiredOperators) || 1),
    0.8,
    1.1
  );
  const calendarOperatorFactor = clamp(Number(machineOperatorCalendarFactor) || 1, 0.3, 2);
  const operatorFactor = clamp(
    globalOperatorFactor * machineOperatorWeight * calendarOperatorFactor,
    0.35,
    1.45
  );

  const capacityRaw = availableHours * productivityApplied;
  const utilizationRate = clamp((Number(machine?.utilizationRate) || 100) / 100, 0, 1.25);
  const simulated = Math.max(0, capacityRaw * operatorFactor * utilizationRate);

  const plan = Math.max(0, Number(planValue) || 0);
  const realized = Math.max(0, Number(realizedValue) || 0);
  const attendancePct = plan > 0 ? (simulated / plan) * 100 : 0;
  const gap = plan - simulated;
  const status = simulated >= plan * (1 - tolerancePercent / 100) ? "BATE" : "NÃO BATE";

  const bottleneckScores = {
    operators: round((1 - operatorFactor) * 100),
    maintenance: round((adjustedMaintenanceHours / Math.max(contractHours, 1)) * 100),
    losses: round(lossesPercent),
    risk: round(riskPenalty * 100),
    planLoad: round(plan > 0 ? Math.max(0, (plan - simulated) / plan) * 100 : 0),
  };
  const bottleneck = pickMainBottleneck(bottleneckScores);

  return {
    id: `${period}_${machine.id}`,
    period,
    machineId: machine.id,
    machineCode: machine.code,
    machineName: machine.name,
    label: `${machine.code} - ${machine.name}`,
    site: machine.site,
    line: machine.line,
    riskLevel: machine.riskLevel,
    plan: round(plan, 1),
    simulated: round(simulated, 1),
    realized: round(realized, 1),
    availableHours: round(availableHours, 1),
    lossesHours: round(lossesHours, 1),
    maintenanceHours: round(adjustedMaintenanceHours, 1),
    productivityApplied: round(productivityApplied, 2),
    utilizationRate: round(utilizationRate * 100, 1),
    contractHoursUsed: round(contractHours, 1),
    operatorFactor: round(operatorFactor, 3),
    calendarOperatorFactor: round(calendarOperatorFactor, 3),
    attendancePct: round(attendancePct, 1),
    gap: round(gap, 1),
    status,
    bottleneck,
    bottleneckScores,
  };
}

export function aggregateRows(rows, tolerancePercent = 0) {
  const list = Array.isArray(rows) ? rows : [];
  const totals = list.reduce(
    (acc, row) => {
      acc.plan += Number(row.plan) || 0;
      acc.simulated += Number(row.simulated) || 0;
      acc.realized += Number(row.realized) || 0;
      acc.bottleneckVotes[row.bottleneck] = (acc.bottleneckVotes[row.bottleneck] || 0) + 1;
      return acc;
    },
    { plan: 0, simulated: 0, realized: 0, bottleneckVotes: {} }
  );

  const attendancePct = totals.plan > 0 ? (totals.simulated / totals.plan) * 100 : 0;
  const realizedPct = totals.plan > 0 ? (totals.realized / totals.plan) * 100 : 0;
  const gap = totals.plan - totals.simulated;
  const status =
    totals.simulated >= totals.plan * (1 - (Number(tolerancePercent) || 0) / 100)
      ? "BATE"
      : "NÃO BATE";

  const sortedBottlenecks = Object.entries(totals.bottleneckVotes).sort(
    (a, b) => b[1] - a[1]
  );
  const mainBottleneck = sortedBottlenecks[0]?.[0] || "Sem gargalo";

  return {
    rowCount: list.length,
    planTotal: round(totals.plan, 1),
    simulatedTotal: round(totals.simulated, 1),
    realizedTotal: round(totals.realized, 1),
    attendancePct: round(attendancePct, 1),
    realizedPct: round(realizedPct, 1),
    gap: round(gap, 1),
    slack: round(Math.max(0, -gap), 1),
    status,
    mainBottleneck,
  };
}
